import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { ELocalStorageKeys } from '../../../core/models/localStorage.model';
import { EAppRole, EDocumentRoles } from '@shared/models';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    router = inject(Router);
    http = inject(HttpClient);
    jwtHelper = inject(JwtHelperService);
    tenant: string = '';
    private appRoles: Set<EAppRole> = new Set();
    #appDocumentRoles: Set<EDocumentRoles> = new Set();
    
    constructor() {
        this.tenant = this.#initTenant();
        this.#initAppRoles();
    }
    
    initAppRoles(): void {
        this.appRoles.clear();
        this.#initAppRoles();
    }

    getTenant(): string {
        return this.tenant?.toLocaleLowerCase();
    }

    getTenantReal(): string {
        const tenant = this.getTenant().toUpperCase();
        if (tenant === 'MEX') return 'CONDUCE';
        if (tenant === 'LTO') return 'IMPULSO';
        return tenant;
    }

    getTenantRolePrefix(): string {
        const tenant = this.getTenant().toUpperCase();
        return tenant === 'MEX' ? 'DAE' : tenant;
    }

    setTenant(current: string) {
        sessionStorage.setItem('tenant', current);
        this.tenant = current;
    }

    isLtoTenant(): boolean {
        return this.tenant === environment.tenants[1];
    }

    #initTenant(): string {
        let path_segment = this.router
            .parseUrl(document.location.pathname)
            .root.children['primary']?.segments[1]?.path.toLowerCase();

        path_segment = path_segment
            ? path_segment
            : sessionStorage.getItem('tenant')
            ? sessionStorage.getItem('tenant')!
            : environment.tenants[0];

        if (!environment.tenants.includes(path_segment)) {
            return '';
        }
        sessionStorage.setItem('tenant', path_segment);
        return path_segment;
    }

    #initAppRoles(): void {
        const token = window.localStorage.getItem(
            ELocalStorageKeys.accessToken
        );
        if (!token) return;

        const tokenRoles = this.getRolesFromToken(token);
        const tenantPrefix = this.getTenantRolePrefix();

        tokenRoles.forEach((role) => {
            const roleUpper = role.toUpperCase();
            if (roleUpper === `${tenantPrefix}_ADMIN`) {
                this.appRoles.add(EAppRole.Admin);
            }

            if (roleUpper === `${tenantPrefix}_PARO_MOTOR_ADMIN`) {
                this.appRoles.add(EAppRole.ParoMotor);
            }

            if (roleUpper === `${tenantPrefix}_USER`) {
                this.appRoles.add(EAppRole.User);
            }

            if (roleUpper === `${tenantPrefix}_DEVELOPER`) {
                this.appRoles.add(EAppRole.Developer);
            }

            if (roleUpper === `${tenantPrefix}_DOCUMENT_ADMIN`) {
                this.#appDocumentRoles.add(EDocumentRoles.Admin);
            }

            if (roleUpper === `${tenantPrefix}_DOCUMENT_CONFIDENTIAL_VIEW`) {
                this.#appDocumentRoles.add(EDocumentRoles.ConfidentialView);
            }

            const realTenant = this.getTenantReal()
            if (realTenant === 'CONDUCE') {
                this.appRoles.add(EAppRole.Conduce)
            }
            if (realTenant === 'IMPULSO') {
                this.appRoles.add(EAppRole.Impulso)
            }
        })
    }

    hasRole(role: EAppRole): boolean {
        return this.appRoles.has(role);
    }

    hasDocumentRole(role: EDocumentRoles): boolean {
        return this.#appDocumentRoles.has(role);
    }

    getAppRoles(): EAppRole[] {
        return Array.from(this.appRoles);
    }

    refreshToken(refresh_token: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        const body = {
            refreshToken: refresh_token,
        };

        return this.http.post<any>(
            `${environment.gateway}/auth/refresh-token`,
            body,
            {
                headers,
            }
        );
    }

    canUserAccessApp(token: string, tenant: string) {
        const role = this.getRequiredRoleForTenant(tenant);
        return this.tokenHasRole(token, role);
    }

    tokenHasRole(token: string, role: string) {
        const user = this.jwtHelper.decodeToken(token);
        return user.scope.includes(role);
    }

    getRolesFromToken(token: string): Array<string> {
        const user = this.jwtHelper.decodeToken(token);
        if (!user || !user.scope) {
            return [];
        }
        if (Array.isArray(user.scope)) {
            return user.scope;
        }
        return user.scope.split(' ');
    }

    getUserRoles(): string[] {
        return Array.from(this.appRoles);
    }

    isAdministrator(): boolean {
        return this.hasRole(EAppRole.Admin);
    }

    isStopEngine(): boolean {
        return this.hasRole(EAppRole.ParoMotor);
    }

    isDeveloper(): boolean {
        return this.hasRole(EAppRole.Developer)
    }

    isDocumentAdmin(): boolean {
        return this.hasDocumentRole(EDocumentRoles.Admin);
    }

    isDocConfidentialView(): boolean {
        return this.hasDocumentRole(EDocumentRoles.ConfidentialView);
    }

    isConduce(): boolean {
        return this.hasRole(EAppRole.Conduce)
    }

    isImpulso(): boolean {
        return this.hasRole(EAppRole.Impulso)
    }

    isAdmin(): boolean {
        return this.hasRole(EAppRole.Admin);
    }

    private getRequiredRoleForTenant(tenant: string) {
        return tenant.toUpperCase() === 'LTO' ? 'LTO_USER' : 'DAE_USER';
    }

    isTokenExpired(token: string): boolean {
        return this.jwtHelper.isTokenExpired(token);
    }

    signout(): void {
        // Limpiar tokens del localStorage
        window.localStorage.removeItem(ELocalStorageKeys.accessToken);
        window.localStorage.removeItem(ELocalStorageKeys.refreshToken);

        // Limpiar sessionStorage
        sessionStorage.clear();

        // Obtener el tenant actual y redirigir con recarga completa
        const tenant = this.getTenant() || 'mex';
        const loginRoute = `${environment.app_base_url}/page/${tenant}/login`;

        window.location.href = loginRoute;
    }
}
